"use client"

import React, { useState, useTransition } from "react"
import { debounce } from "next/dist/server/utils"
import { useRouter } from "next/navigation"
import { createWishlistAction } from "@/actions/wishlistActions"
import { CheckCircleIcon, CircleAlertIcon, LoaderCircle, PlusIcon, SaveIcon } from "lucide-react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import { z } from "zod"

import { cn } from "@/lib/utils"
import { Button, Input } from "@/components/ui/components"
import { UbAccountWishlistsViewStoryblok } from "@/components/storyblok/component-types"

export function AccountWishlistDetailNewForm(props: {
  blok: UbAccountWishlistsViewStoryblok
  onCancelAction?: () => void
}) {
  const newWishlistSchema = z.object({
    title: z.string().nonempty(props.blok.new_wishlist_form_error_empty_name /*"Name ist erforderlich"*/),
  })

  type NewWishlistFormValues = z.infer<typeof newWishlistSchema>
  const [values, setValues] = useState<NewWishlistFormValues>({
    title: "",
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isPending, startTransition] = useTransition()
  const router = useRouter()
  const debouncedValidate = debounce(validate, 500)

  function setField<K extends keyof NewWishlistFormValues>(key: K, val: NewWishlistFormValues[K]) {
    setValues((v) => {
      const fieldValues = { ...v, [key]: val }
      debouncedValidate(fieldValues)
      return fieldValues
    })
  }

  function onCancel() {
    setValues({ title: "" })
    setErrors({})
    if (props.onCancelAction) {
      props.onCancelAction()
    }
  }
  function validate(fieldValues) {
    setErrors({})
    const parsed = newWishlistSchema.safeParse(fieldValues)
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((issue) => [issue.path.join("."), issue.message])))
    }
    return parsed.success
  }

  function onCreate() {
    if (!validate(values)) {
      return
    }

    startTransition(async () => {
      await createWishlistAction(values.title)
        .then((wishlistId) => {
          toast.success(props.blok.label_new_wishlist_form_success /* ?? "Daten wurden erfolgreich gespeichert"*/, {
            icon: <CheckCircleIcon className={"size-4 text-green-500"} />,
            description: "",
          })
          router.push(`/account/wishlist/${wishlistId}`)
        })
        .catch((err: Error) => {
          toast.error(err.message)
        })
    })
  }

  return (
    <>
      <div className={"flex flex-col gap-4"}>
        <div className={"flex flex-col gap-1"}>
          <Input
            id={"title"}
            type={"text"}
            value={values.title}
            onChange={(e) => setField("title", e.target.value)}
            error={errors["title"]}
            placeholder={props.blok.label_new_wishlist_form_title_placeholder /* ?? "Name der Merkliste"*/}
          />
          <p className={cn("hidden text-sm text-red-600", { block: errors["title"] })}>
            <CircleAlertIcon className={"-mt-0.5 inline h-4"} />
            {errors["title"]}
          </p>
        </div>
        <div className={"flex flex-wrap justify-between gap-2"}>
          <Button type="button" variant="outline" className={"max-md:hidden"} onClick={onCancel}>
            <PlusIcon className={"inline size-5 rotate-45 pt-0.5"} />
            {props.blok.label_new_wishlist_form_cancel /*?? "Abbrechen"*/}
          </Button>
          <Button type="button" className={"max-md:w-full"} onClick={onCreate} disabled={isPending}>
            {isPending ? (
              <LoaderCircle className={"inline size-5 pt-0.5 motion-safe:animate-spin"} />
            ) : (
              <SaveIcon className={"inline size-5 pt-0.5"} />
            )}
            <span className={"ml-2"}>
              {
                isPending
                  ? props.blok?.label_new_wishlist_form_creating /*?? "Legt an..."*/
                  : props.blok?.label_new_wishlist_form_create /*?? "Merkliste anlegen"*/
              }
            </span>
          </Button>
        </div>
      </div>
    </>
  )
}
